import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export type ScheduleColor = 'red' | 'orange' | 'green' | 'blue' | 'pink';

export const SCHEDULE_COLORS: { value: ScheduleColor; label: string; bg: string; text: string; border: string }[] = [
  { value: 'red',    label: '빨강', bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-200' },
  { value: 'orange', label: '주황', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  { value: 'green',  label: '초록', bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200' },
  { value: 'blue',   label: '파랑', bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-200' },
  { value: 'pink',   label: '분홍', bg: 'bg-pink-100',   text: 'text-pink-700',   border: 'border-pink-200' },
];

export const getColorStyle = (color?: string) =>
  SCHEDULE_COLORS.find(c => c.value === color) || SCHEDULE_COLORS[2]; // default: green

export interface CenterSchedule {
  id: number;
  event_date: string;
  title: string;
  is_holiday: boolean;
  color?: ScheduleColor;
  created_at: string;
  updated_at: string;
}

interface CenterScheduleContextType {
  schedules: CenterSchedule[];
  loading: boolean;
  fetchSchedulesByMonth: (year: number, month: number) => Promise<void>;
  addSchedule: (schedule: { event_date: string; title: string; is_holiday: boolean; color?: ScheduleColor }) => Promise<void>;
  updateSchedule: (id: number, updates: { title?: string; is_holiday?: boolean; color?: ScheduleColor }) => Promise<void>;
  deleteSchedule: (id: number) => Promise<void>;
}

const CenterScheduleContext = createContext<CenterScheduleContextType | undefined>(undefined);

export const CenterScheduleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [schedules, setSchedules] = useState<CenterSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentYear, setCurrentYear] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(0);

  const fetchSchedulesByMonth = useCallback(async (year: number, month: number) => {
    try {
      setLoading(true);
      setCurrentYear(year);
      setCurrentMonth(month);

      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const { data, error } = await supabase
        .from('center_schedules')
        .select('*')
        .gte('event_date', startDate)
        .lte('event_date', endDate)
        .order('event_date', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;
      setSchedules(data || []);
    } catch (error) {
      console.error('Error fetching center schedules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    if (currentYear && currentMonth) {
      await fetchSchedulesByMonth(currentYear, currentMonth);
    }
  }, [currentYear, currentMonth, fetchSchedulesByMonth]);

  const addSchedule = async (schedule: { event_date: string; title: string; is_holiday: boolean; color?: ScheduleColor }) => {
    try {
      const { error } = await supabase
        .from('center_schedules')
        .insert(schedule);

      if (error) throw error;
      await refetch();
    } catch (error) {
      console.error('Error adding schedule:', error);
      throw error;
    }
  };

  const updateSchedule = async (id: number, updates: { title?: string; is_holiday?: boolean; color?: ScheduleColor }) => {
    try {
      const { error } = await supabase
        .from('center_schedules')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await refetch();
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  };

  const deleteSchedule = async (id: number) => {
    try {
      const { error } = await supabase
        .from('center_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSchedules(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  };

  return (
    <CenterScheduleContext.Provider value={{
      schedules,
      loading,
      fetchSchedulesByMonth,
      addSchedule,
      updateSchedule,
      deleteSchedule
    }}>
      {children}
    </CenterScheduleContext.Provider>
  );
};

export const useCenterSchedule = () => {
  const context = useContext(CenterScheduleContext);
  if (context === undefined) {
    throw new Error('useCenterSchedule must be used within a CenterScheduleProvider');
  }
  return context;
};
